import Button from '@components/Button';
import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import GiftTypeNotes from '@components/gift/GiftTypeNotes';
import Header from '@components/Header';
import Loading from '@components/Loading';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import OrderInfo from './components/OrderInfo';
import ProductCard from './components/ProductCard';
import useGiftOrderResult from './hooks/useGiftOrderResult';
import { canCancelGiftOrder } from './utils';

interface Params {
    giftOrderNo: string;
}

declare module '@stackflow/config' {
    interface Register {
        GiftSentActivity: Params;
    }
}

const GiftSentActivity: ActivityComponentType<'GiftSentActivity'> = ({ params }) => {
    const { giftOrderNo } = params;
    const { t } = useTranslation('gift');
    const { push } = useFlow();
    const { data: order } = useGiftOrderResult(giftOrderNo);
    const showCancel = order ? canCancelGiftOrder(order) : false;

    const handleCancel = () => {
        if (!order) return;
        push('CancelPaymentModal', { giftOrderId: order.id, giftOrderNo });
    };

    return (
        <AppScreen>
            <CtaLayout
                header={<Header title={t('giftSent.title')} />}
                footer={showCancel ? (
                    <Button fullWidth onClick={handleCancel}>
                        {t('giftSent.cancelPayment')}
                    </Button>
                ) : null}
                noFooterBorder>
                <Page>
                    <Content fullWidth spacing={3}>
                        {!order && <Loading size='small' />}
                        {order && (
                            <>
                                <ProductCard
                                    name={order.giftItem.name}
                                    imageUrl={order.giftItem.imageUrl}
                                    message={order.message}
                                />
                                <InfoCard>
                                    <OrderInfo order={order} />
                                </InfoCard>
                            </>
                        )}
                    </Content>
                    {order && <GiftTypeNotes giftItemType={order.giftItem.giftItemType} />}
                </Page>
            </CtaLayout>
        </AppScreen>
    );
};

const Page = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: var(--spacing-3);
    background: ${colors.gray1};
    flex: 1;
`;

const Content = styled(Column)`
    padding: var(--spacing-5) var(--spacing-4)  0;
`;

const InfoCard = styled.div`
    width: 100%;
    padding: var(--spacing-5) var(--spacing-4);
    border-radius: 8px;
    background: ${colors.white};
`;
export default GiftSentActivity;
